import {IconButton, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useIsMobileView} from '../../hooks/useIsMobileView';
import type {EagColumnOrGroup} from '../../types/EagColumnGroup';
import type {Filters} from '../../types/Filters';
import type {QueryFilterObject} from '../../types/QueryFilterObject';
import {buildActiveFilterChips} from '../../utils/buildActiveFilterChips';
import {flattenColumnConfig} from '../../utils/flattenColumnConfig';
import {FilteredByLabel} from './FilteredByLabel';

type ActiveFilterBarProps = {
  filterValues: Filters;
  columnConfig: EagColumnOrGroup[];
  /** Resolved async-filter labels keyed by suggestId then option value (the store's
   *  `suggestionLabelsCache`). Lets async filter chips (e.g. Branch) show a name
   *  instead of the raw stored key. */
  suggestionLabels?: Map<string, Map<string, string>>;
  staticFilterKeys?: QueryFilterObject;
  onRemoveFilter: (key: string) => void;
  onClearAll: () => void;
  onSaveAsView?: () => void;
} & TestIdProps;

/**
 * Reshapes the store's async-label cache (suggestId → value → label, populated by
 * useResolveSuggestionFilterLabels) into the column-key-keyed `{key, label}[]` option
 * lists `buildActiveFilterChips` resolves MULTI_CHOICE_ASYNC chip values against, so
 * async filter chips (e.g. Branch) show a name instead of the raw stored key.
 */
const buildExtraOptionsByColumnKey = (
  columnConfig: EagColumnOrGroup[],
  suggestionLabels: Map<string, Map<string, string>> | undefined
): Map<string, Array<{key: string; label: string}>> => {
  const byColumnKey = new Map<string, Array<{key: string; label: string}>>();
  if (!suggestionLabels) {
    return byColumnKey;
  }
  flattenColumnConfig(columnConfig).forEach((column) => {
    const suggestId = column.filter?.suggestId;
    const resolved = suggestId ? suggestionLabels.get(suggestId) : undefined;
    if (resolved) {
      byColumnKey.set(
        column.key,
        Array.from(resolved, ([key, label]) => ({key, label}))
      );
    }
  });
  return byColumnKey;
};

/**
 * Renders a "Filtered by" row with one chip per active filter plus a "Save as view" action and
 * a "Clear all" control that resets every filter at once.
 *
 * Returns null when there are no active filters so the bar takes no space.
 */
export function ActiveFilterBar(props: ActiveFilterBarProps) {
  const t = useTranslationContext();
  const isMobile = useIsMobileView();
  const excludeKeys = props.staticFilterKeys
    ? new Set(Object.keys(props.staticFilterKeys))
    : undefined;
  const extraOptionsByColumnKey = buildExtraOptionsByColumnKey(
    props.columnConfig,
    props.suggestionLabels
  );
  const chips = buildActiveFilterChips(props.filterValues, props.columnConfig, {
    searchLabel: t('page.datagrid.labels.search'),
    excludeKeys,
    extraOptionsByColumnKey,
  });

  // Mobile renders the filter icon's count badge + the filter sheet instead — the chip row
  // wraps/stacks and breaks at this width, and duplicates that count. All hooks above run
  // unconditionally regardless of viewport; this early return comes after every hook call.
  if (isMobile) {
    return null;
  }

  if (chips.length === 0) {
    return null;
  }

  const rowClassName = 'flex items-center gap-4 px-[14px] py-[9px] bg-[#F3F9FF]';
  const chipClassName =
    'inline-flex items-center gap-1.5 rounded bg-white pl-2.5 pr-1.5 h-[26px] text-xs text-[#091E42]';
  // Hand-rolled link buttons: platform Button renders small+link at 12px but
  // small+dangerLink at 14px (no combined size-variant rule for dangerLink),
  // which made these actions visibly mismatched. All use the bar's 12px
  // (text-xs) typography instead. fontFamily: raw <button> elements get the UA's
  // own button font — this bundle's Tailwind preflight doesn't normalize it, so
  // inherit explicitly.
  const linkButtonClassName =
    'text-xs font-medium hover:underline bg-transparent border-none cursor-pointer p-0 whitespace-nowrap';

  return (
    // bg/text arbitrary-colour utilities render fine in this repo's Tailwind
    // bundle, but border-width utilities compute to 0px — so every 1px border in
    // this bar (each row's bottom divider and each chip outline) is set inline.
    <div>
      <Show when={chips.length > 0}>
        <div className={rowClassName} style={{borderBottom: '1px solid #BFE0FF'}}>
          <FilteredByLabel label={t('page.datagrid.labels.filteredBy')} />
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
            {chips.map((chip) => (
              <span key={chip.key} className={chipClassName} style={{border: '1px solid #BFE0FF'}}>
                <span className="font-semibold">{chip.label}</span>
                <span>{chip.valueLabel}</span>
                <IconButton
                  icon="navigation/close"
                  size="small"
                  aria-label={t('page.datagrid.button.removeFilter')}
                  onClick={() => props.onRemoveFilter(chip.key)}
                  data-testid={suffixTestId(`activeFilterChip-${chip.key}-remove`, props)}
                />
              </span>
            ))}
          </div>
          <div
            className="flex items-center gap-[14px] shrink-0 self-stretch pl-4"
            style={{borderLeft: '1px solid #C1C7D0'}}
          >
            <Show when={props.onSaveAsView}>
              <button
                type="button"
                className={linkButtonClassName}
                style={{color: '#0052CC', fontFamily: 'inherit'}}
                onClick={props.onSaveAsView}
                data-testid={suffixTestId('activeFilterBar-saveAsView', props)}
              >
                {t('page.datagrid.button.saveAsView')}
              </button>
            </Show>
            <button
              type="button"
              className={linkButtonClassName}
              style={{color: '#DE350B', fontFamily: 'inherit'}}
              onClick={props.onClearAll}
              data-testid={suffixTestId('activeFilterBar-clearAll', props)}
            >
              {t('page.datagrid.labels.clearAll')}
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
}
